import {createSelector} from 'reselect';
import {weakMemo} from '@packages/utilities/memo';
import {Component, Plugins, Expressions} from '@packages/systems/wfdl';
import {
  qualify,
  namesEqual,
  getName,
  QualifiedMap,
  Qualified,
  Identifier,
} from '@packages/systems/wfdl-Qualified';
import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {
  StyleRuleLinkingUiState,
  type ExternalState,
} from '@packages/systems/designer/external';
import type {
  PageRecordType,
  PageState,
} from '@packages/systems/core/records/PageRecord';
import {
  getGuidNullable,
  getGuid,
  type NativeId,
  toNativeIdPath,
} from '@packages/systems/core/utils/NativeIdUtils';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import last from 'lodash/last';
import {
  contains,
  getPageComponentFromComponentMap,
  pageComponentType,
  getComponentInstRootElement,
  isUserComponent,
  getCurrentComponentType,
  getCurrentComponentRender,
  getComponentElementById,
} from '@packages/utilities/core-elements';
import {
  getElementFromNodeInInstanceNativeId,
  getTargetElementOrInstance,
  getOutermostSymbolInstance,
  isSlotDefaultSelection,
  resolveSlotDefaultComponentBoundary,
  resolveSlotDefaultOwningInstanceRootPath,
  resolveSlotDefaultPathContext,
  findSlotDefaultElement,
  mkMissedFocusSegmentResolver,
  projectSlotDefaultIntoRender,
  getRelativePath,
  isPathContainingInstances,
  isPathToInstanceDescendant,
  getParentNativeIdPath,
  expandUserComponents,
  getBody,
  isElementInsideSlot,
  findElementDescendant,
  componentInstanceHasCyclicSlotAncestor,
  isCodeComponentInst,
  isEditableCodeComponentInst,
  isCodeComponentWithSourceComponent,
  isRichTextDescendant,
  isComponentInsidePageSlot,
  getSlotResetTarget,
} from '@packages/utilities/platform';
import {
  isComponentOnlyDropTarget,
  isRichText,
} from '@packages/utilities/elementTypeUtils';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {
  EElement,
  ERecord,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {parseVariantStateKey} from '@packages/systems/style-legacy/selectors';
import {generateVariantDataMap} from '@packages/systems/component-style-variants';
import {NavigatorStateType} from '@packages/shared/stores/NavigatorStateType';
import {selectIsCodeComponentCanvas} from '@packages/systems/component-canvas/universal';

/**
 * Here are the primary selectors provided by core for interfacing with platform.
 *
 * The structures you retrieve here can be used with the WFDL package to create higher level
 * abstractions and end-user features. They come in two forms:
 *
 * 1. React hook, for working with modern React components. `use<Noun>`, e.g. `useComponents`, `useSelectedElement` - stored in the PlatformHooks.js
 * 2. As a selector, for working with legacy Flux code. `select<Noun>`, e.g. `selectComponents` & `selectSelectedElement` - stored in the @packages/utilities/platform-selectors
 */

/**
 * When a user is updating their design, a staged version of their design
 * is created. This is called the "live" component map because it is what is reflected
 * in the Designer. If the user "confirms" their change, by dropping the element or
 * by a Data Type transaction completely, the staged component map will become the
 * next persisted component map.
 *
 * Generally, if you are reading from the component map you'll want to use this function
 * to select the current component map that the user is working on and seeing.
 */
export const getLiveComponentMap = (
  designerState: DesignerStateType
): Component.ComponentMap => {
  return designerState.stagedComponents || designerState.components;
};

// Safe since `getLiveComponentMap` doesn't return a new object:
// eslint-disable-next-line webflow/selector-memoization
export const selectComponents = ({
  DesignerStore,
}: {
  DesignerStore: DesignerStateType;
}): Component.ComponentMap => {
  return getLiveComponentMap(DesignerStore);
};

export const selectComponentNameMap = createSelector(
  selectComponents,
  (componentMap): Record<string, string> => {
    const map: Record<string, string> = {};
    componentMap.forEach((component, qualifiedType) => {
      const id = getName(qualifiedType);
      if (id === 'page') return;
      map[id] = component.displayName;
    });
    return map;
  }
);

const DOMType = qualify(Plugins.name('Builtin'), Component.name('DOM'));
const fakeComponent = Component.create({
  render: EElement({id: 'fake-component', type: DOMType, data: ERecord({})}),
});

/**
 * Returns the currently focused component or a dummy.
 */
export const selectCurrentComponent = (state: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}): Component.ComponentType => {
  const currentComponentMap = getLiveComponentMap(state.DesignerStore);
  const componentType = selectCurrentComponentType(state);
  const currentComponent = currentComponentMap.get(componentType);
  return currentComponent || fakeComponent;
};

type FocusedComponentEntry = {
  component: Component.ComponentType;
  componentType: Plugins.QualifiedElement;
  render: Expression;
};

function getNextFocusedComponentEntry(
  componentMap: Component.ComponentMap,
  currentComponent: Component.ComponentType,
  focusedSymbolNativeId: NativeId
): FocusedComponentEntry | null | undefined {
  const symbolInstance = getComponentElementById(
    currentComponent,
    getGuid(focusedSymbolNativeId)
  );
  if (!symbolInstance) {
    return null;
  }

  const nextComponent = componentMap.get(symbolInstance.type);

  return nextComponent
    ? {
        component: nextComponent,
        componentType: symbolInstance.type,
        render: Component.getRender(nextComponent),
      }
    : null;
}

/**
 * Returns all components on the focused symbol path including page component
 *
 * Walks focused symbol native id path starting from page component and
 * returns and array of form [pageComponent, component1, ..., componentN].
 *
 * If the focused symbol path is corrupt, i.e. it contains a focused symbol
 * native id that has no corresponding valid symbol instance, we return null.
 */
function getFocusedComponentEntries(
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: Array<NativeId>
): Array<FocusedComponentEntry> | null {
  const pageComponent = getPageComponentFromComponentMap(componentMap);

  if (!pageComponent) {
    return null;
  }

  const components = [
    {
      component: pageComponent,
      componentType: pageComponentType,
      render: Component.getRender(pageComponent),
    },
  ];

  for (const focusedSymbolNativeId of focusedSymbolNativeIdPath) {
    const currentComponent = components[components.length - 1]?.component;
    if (!currentComponent) return null;

    const nextComponent = getNextFocusedComponentEntry(
      componentMap,
      currentComponent,
      focusedSymbolNativeId
    );

    if (!nextComponent) {
      // bail out, focused symbol path was corrupt
      return null;
    }

    components.push(nextComponent);
  }

  return components;
}

/**
 * Returns the component entry that owns a given element within the focused
 * component chain. Walks the focused symbol native id path and returns the
 * first entry whose render tree contains the specified element id.
 */
export function getElementComponentEntry(
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: Array<NativeId>,
  elementId: string
): FocusedComponentEntry | null {
  const componentEntries = getFocusedComponentEntries(
    componentMap,
    focusedSymbolNativeIdPath
  );

  if (componentEntries === null) {
    return null;
  }

  // Component canvas can target either the page component or any component on
  // the focused instance path, so return the first render on that chain that
  // actually owns this element id.
  for (const componentEntry of componentEntries) {
    if (Expressions.getElementById(componentEntry.render, elementId)) {
      return componentEntry;
    }
  }

  return null;
}

/**
 * Returns first component from focused components that contains element
 *
 * Walks the focused symbol native id path starting from page component.
 * If the focused symbol path is corrupt, or the element is not found from
 * the page component or any of the focused components, we return a fake
 * component. Otherwise, we return the first encountered component that
 * contains an element that matches the provided element id.
 *
 * Warning: You should probably use selectCurrentComponent instead of this!
 */
export function getElementComponent(
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: Array<NativeId>,
  elementId: string
): Component.ComponentType {
  const componentEntry = getElementComponentEntry(
    componentMap,
    focusedSymbolNativeIdPath,
    elementId
  );

  return componentEntry?.component || fakeComponent;
}

/**
 * Flux state selector version of `getCurrentComponentType`
 */
// Safe since this doesn't return a new object:
// eslint-disable-next-line webflow/selector-memoization
export const selectCurrentComponentType = (state: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}): Plugins.QualifiedElement => {
  const currentComponentMap = getLiveComponentMap(state.DesignerStore);
  const {focusedSymbolNativeIdPath} = state.UiNodeStore;

  return getCurrentComponentType(
    currentComponentMap,
    focusedSymbolNativeIdPath,
    mkMissedFocusSegmentResolver(currentComponentMap)
  );
};

/**
 * Flux state selector version of `getCurrentComponentRender`
 */
export const selectCurrentComponentRender: (state: {
  UiNodeStore: UiNodeStateType;
  DesignerStore: DesignerStateType;
}) => Expression | null = createSelector(
  selectComponents,
  (state: {DesignerStore: DesignerStateType; UiNodeStore: UiNodeStateType}) =>
    state.UiNodeStore.focusedSymbolNativeIdPath,
  (
    componentMap: Component.ComponentMap,
    focusedSymbolNativeIdPath: Array<NativeId>
  ): Expression | null =>
    getCurrentComponentRender(
      componentMap,
      focusedSymbolNativeIdPath,
      mkMissedFocusSegmentResolver(componentMap)
    )
);

/**
 * Returns the id of the current focused user component.
 * If there isn't a focused user component, returns `null`
 */
export const selectCurrentUserComponentId = createSelector(
  selectCurrentComponentRender,
  selectCurrentComponentType,
  (currentComponentRender, currentComponentType) => {
    if (!isUserComponent(currentComponentType)) {
      return null;
    }

    return currentComponentRender
      ? Expressions.getElement(currentComponentRender)?.id
      : null;
  }
);

/**
 * Retrieve the current page component.
 */
// Safe since this doesn't return a new object:
// eslint-disable-next-line webflow/selector-memoization
export const selectCurrentPageComponent = ({
  DesignerStore,
}: {
  DesignerStore: DesignerStateType;
}): Component.ComponentType | undefined => {
  const componentMap = getLiveComponentMap(DesignerStore);
  return getPageComponentFromComponentMap(componentMap);
};

export const selectCurrentPageId = (state: {
  DesignerStore: DesignerStateType;
}) => state.DesignerStore.currentPageId;

export const selectCurrentPage = createSelector(
  (state: {PageStore: PageState}) => state.PageStore,
  (state: {DesignerStore: DesignerStateType}) =>
    state.DesignerStore.currentPageId,
  (pageState, currentPageId) =>
    (pageState.getIn(['staticPages', currentPageId]) ||
      pageState.getIn(['dynamicPages', currentPageId]) ||
      pageState.getIn(['componentPages', currentPageId])) as
      | PageRecordType
      | undefined
);

/**
 * Takes a provided `rootComponent` and `focusedSymbolNativeIdPath` and returns
 * a new `rootComponent.render` with its focused instances replaced with their
 * associated backing Component render element.
 */
export const getRootElementWithFocusedInstancesExpanded = (
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: Array<NativeId>,
  plugins?: Plugins.PluginsType
): Expression | null => {
  const rootComponent = getPageComponentFromComponentMap(componentMap);

  if (!rootComponent) {
    return null;
  }

  let acc: Expression | null = Component.getRender(rootComponent);
  // The component owning the segment being resolved. Only a slot default needs
  // it, to find the content on that component's `defaultData`.
  let ownerComponentType: Plugins.QualifiedElement = pageComponentType;
  let ownerInstance: ExpressionElement | null = null;

  for (const focusedNativeId of focusedSymbolNativeIdPath) {
    const symbolId = getGuidNullable(focusedNativeId);
    if (acc == null || symbolId === null) return null;

    let element = Expressions.getElementById(acc, symbolId);

    if (element === null) {
      // A slot override is authored on the instance's data, so it is already in
      // this tree. A default is not, so project it in before walking through it.
      const projected = projectSlotDefaultIntoRender(
        plugins ?? null,
        componentMap,
        acc,
        ownerComponentType,
        symbolId,
        ownerInstance
      );
      if (projected) {
        acc = projected.expression;
        element = projected.element;
      }
    }

    if (element === null) return null;

    const value = getComponentInstRootElement(componentMap)(element);

    if (!value) return null;

    ownerComponentType = element.type;
    ownerInstance = element;
    acc = Expressions.updateElement(element.id, () => value, acc);
  }

  return acc;
};

const _allComponentMapElements = weakMemo(
  (componentMap: Component.ComponentMap) =>
    componentMap.reduce<Record<string, ExpressionElement>>(
      (acc, component: Component.ComponentType) => {
        const render = Component.getRender(component);
        const elements = Expressions.allElements(render);

        for (const element in elements) {
          const elementValue = elements[element];
          if (elementValue) {
            acc[element] = elementValue;
          }
        }

        return acc;
      },
      {}
    )
);

/**
 * Traverse through entire componentMap and retrieve an element by id.
 *
 * WARNING: Do not use this function; it does not account for duplicate element
 * IDs between components. Use `getElementFromComponentMap` instead.
 */
export const getElementByIdFromComponentMap: (
  componentMap: Component.ComponentMap,
  id: string
) => ExpressionElement | null = (componentMap, id) => {
  const index = _allComponentMapElements(componentMap);
  const element = index[id];
  return element ? element : null;
};

export const getElementFromComponentMap = (
  componentMap: Component.ComponentMap,
  componentId: Qualified<Component.ComponentName>,
  elementId: Identifier
) => {
  const component = componentMap.get(componentId);
  if (!component) return null;
  return Expressions.getElementById(Component.getRender(component), elementId);
};
/**
 * Flux selector version of `getRootElementWithFocusedInstancesExpanded`
 */
export const selectRootElementWithFocusedInstancesExpanded: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: StyleRuleLinkingUiState;
}) => null | Expression = createSelector(
  selectComponents,
  (state: {
    DesignerStore: DesignerStateType;
    UiNodeStore: StyleRuleLinkingUiState;
  }) => state.UiNodeStore.focusedSymbolNativeIdPath,
  (state: {DesignerStore: DesignerStateType}) => state.DesignerStore.plugins,
  (
    componentMap: Component.ComponentMap,
    focusedSymbolNativeIdPath: Array<NativeId>,
    plugins: Plugins.PluginsType
  ): Expression | null => {
    return getRootElementWithFocusedInstancesExpanded(
      componentMap,
      focusedSymbolNativeIdPath,
      plugins
    );
  }
);

/**
 * Returns the NativeId of the element that is currently selected.
 */
export const selectSelectedNodeNativeId = (state: {
  UiNodeStore: StyleRuleLinkingUiState;
}) => state.UiNodeStore.selectedNodeNativeId;

/**
 * Returns the element that is currently selected within the focused component.
 *
 * This selector only looks inside the currently focused component and looks for
 * the element that matches `UiNodeStore.selectedNodeNativeId`. Prefer this over
 * `selectElementFromSelectedNodeNativeId` because performant due to no
 * component instance expansion and the subsequent larger search space for the
 * element.
 */
export const selectSelectedElementInFocusedComponent = createSelector(
  selectCurrentComponentType,
  selectComponents,
  (state: {UiNodeStore: UiNodeStateType}) =>
    getGuidNullable(state.UiNodeStore.selectedNodeNativeId),
  (currentComponentType, componentMap, elementId) => {
    if (!elementId) return null;
    return getElementFromComponentMap(
      componentMap,
      currentComponentType,
      elementId
    );
  }
);

export const getPluginsFromState = (state: {
  DesignerStore: DesignerStateType;
}): Plugins.PluginsType => state.DesignerStore.plugins;

export const selectSelectedNativeIdPath = (state: {
  UiNodeStore: StyleRuleLinkingUiState;
}): Array<NativeId> => {
  return state.UiNodeStore.selectedNativeIdPath;
};

/**
 * Returns the element that the user is working with. If a component map is staged that
 * will be used. The currently focused component is also leveraged in the look up to
 * support working on components directly and improve look up time.
 *
 * In case if the element is selected inside the component instance and is the root element,
 * it will return the root element itself.
 */
export const selectSelectedElement = createSelector(
  selectRootElementWithFocusedInstancesExpanded,
  selectComponents,
  (state: {UiNodeStore: UiNodeStateType}) =>
    getGuidNullable(state.UiNodeStore.selectedNodeNativeId),
  selectSelectedNativeIdPath,
  getPluginsFromState,
  (
    rootElement,
    componentMap,
    selectedNodeNativeId,
    selectedNativeIdPath,
    plugins
  ) => {
    if (selectedNativeIdPath?.length > 0 && componentMap) {
      const element = getElementFromNodeInInstanceNativeId(
        selectedNativeIdPath,
        componentMap,
        plugins
      );
      return element;
    }
    return selectedNodeNativeId != null && rootElement
      ? Expressions.getElementById(rootElement, selectedNodeNativeId)
      : null;
  }
);

/**
 * Returns the element that the user is working with. If a component map is staged that
 * will be used. The currently focused component is also leveraged in the look up to
 * support working on components directly and improve look up time.
 *
 * In case if the element is selected inside the component instance and is the root element,
 * it will return the component instance element.
 *
 * See also {@link getTargetElementOrInstance} for implementation details
 */
export const selectTargetElementOrInstance = createSelector(
  selectRootElementWithFocusedInstancesExpanded,
  selectComponents,
  (state: {UiNodeStore: UiNodeStateType}) =>
    getGuidNullable(state.UiNodeStore.selectedNodeNativeId),
  selectSelectedNativeIdPath,
  getPluginsFromState,
  (
    rootElement,
    componentMap,
    selectedNodeNativeId,
    selectedNativeIdPath,
    plugins
  ) => {
    if (selectedNativeIdPath.length > 0 && componentMap) {
      const element = getTargetElementOrInstance(
        selectedNativeIdPath,
        componentMap,
        plugins
      );
      return element;
    }
    return selectedNodeNativeId != null && rootElement
      ? Expressions.getElementById(rootElement, selectedNodeNativeId)
      : null;
  }
);

export const getHoleySettings: (state: {
  DesignerStore: DesignerStateType;
}) => QualifiedMap<
  Component.ComponentName,
  {
    [key: string]: Expression;
  }
> = (state: {DesignerStore: DesignerStateType}) =>
  state.DesignerStore.holeySettings;

export const selectHoveredNativeIdPath = (state: {
  UiNodeStore: UiNodeStateType;
}): Array<NativeId> => {
  return state.UiNodeStore.hoveredNativeIdPath;
};

export const selectFocusedSymbolNativeIdPath = (state: {
  UiNodeStore: UiNodeStateType;
}): Array<NativeId> => {
  return state.UiNodeStore.focusedSymbolNativeIdPath;
};

export const selectExpandedItems = (state: {
  NavigatorStore: NavigatorStateType;
}) => {
  return state.NavigatorStore.expandedNodes;
};

/**
 * Returns true if a component instance is selected, both for the
 * top level instance and for "in instance" selections.
 */
export const hasSelectedComponentInstance = createSelector(
  selectSelectedNativeIdPath,
  selectFocusedSymbolNativeIdPath,
  (selectedNativeIdPath, focusedSymbolNativeIdPath) => {
    const relativePath = getRelativePath(
      selectedNativeIdPath,
      focusedSymbolNativeIdPath
    );
    return isPathContainingInstances(relativePath);
  }
);

/**
 * Returns all the component root elements except the page component
 */
export const getComponentMapWithoutPage = (
  DesignerStore: DesignerStateType
): Component.ComponentMap =>
  getLiveComponentMap(DesignerStore).filter(
    (component, name) => !namesEqual(name, pageComponentType)
  );

// Safe since this doesn't return an object:
// eslint-disable-next-line webflow/selector-memoization
export const selectSelectedNodeGuid = (state: {
  UiNodeStore: UiNodeStateType;
}): string | null =>
  getNodeGuidFromNativeIdPath(getSelectedNativeIdPath(state));

/**
 * Returns true when the selected element is a descendant of the
 * instance, and is not the the root of the instance.
 */
export const hasSelectedNodeInInstance = createSelector(
  selectSelectedNativeIdPath,
  selectFocusedSymbolNativeIdPath,
  hasSelectedComponentInstance,
  selectComponents,
  getPluginsFromState,
  (
    selectedNativeIdPath,
    focusedSymbolNativeIdPath,
    isComponentInstanceSelected,
    componentMap,
    plugins
  ) => {
    if (!isComponentInstanceSelected) return false;

    const componentBoundary = resolveSlotDefaultComponentBoundary(
      selectedNativeIdPath,
      componentMap,
      plugins
    );
    if (!componentBoundary) {
      return isPathToInstanceDescendant(
        selectedNativeIdPath,
        componentMap,
        focusedSymbolNativeIdPath
      );
    }
    if (focusedSymbolNativeIdPath.length > 0) return false;

    return isPathToInstanceDescendant(
      selectedNativeIdPath,
      componentMap,
      componentBoundary.nativeIdPathBoundary
    );
  }
);

/**
 * Gets the node guid from a nativeIdPath.
 */
export function getNodeGuidFromNativeIdPath(
  nativeIdPath: NativeId[]
): string | null {
  return nativeIdPath.length > 0 ? getGuidNullable(last(nativeIdPath)) : null;
}

/**
 * Gets the currently selected element's nativeIdPath.
 */
export const getSelectedNativeIdPath = createSelector(
  selectSelectedNodeNativeId,
  selectSelectedNativeIdPath,
  (selectedNodeNativeId, selectedNativeIdPath) => {
    return selectedNativeIdPath?.length
      ? selectedNativeIdPath
      : toNativeIdPath(
          selectedNodeNativeId ? [selectedNodeNativeId] : emptyArray
        );
  }
);

/**
 * Returns the nativeId and element of the selected node.
 * Uses selectSelectedElement to get the selectedElement, and adds the
 * selectedNodeNativeId.
 */
export const selectSelectedNativeIdAndElement: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => {
  nativeId: string | null;
  element: ExpressionElement | null;
} = createSelector(
  selectSelectedNodeNativeId,
  selectSelectedElement,
  (selectedNodeNativeId, element) => {
    return {nativeId: selectedNodeNativeId, element};
  }
);

/**
 * Returns the parent nativeId and element of the selected node.
 */
export const selectSelectedParentNativeIdAndElement: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => {
  nativeId: string | null;
  nativeIdPath: NativeId[] | null;
  element: ExpressionElement | null;
} = createSelector(
  selectSelectedNativeIdAndElement,
  getSelectedNativeIdPath,
  selectCurrentComponent,
  selectComponents,
  (state: {UiNodeStore: UiNodeStateType}) =>
    state.UiNodeStore.focusedSymbolNativeIdPath,
  (
    {element: selectedElement},
    selectedNativeIdPath,
    currentComponent,
    liveComponentMap,
    focusedSymbolNativeIdPath
  ) => {
    const parentPath = getParentNativeIdPath(
      selectedNativeIdPath,
      liveComponentMap,
      focusedSymbolNativeIdPath
    );

    const selectedParentNodeNativeId = last(parentPath) || null;

    const currentComponentRender = currentComponent
      ? Component.getRender(currentComponent)
      : null;

    const parentElement =
      selectedElement && currentComponentRender
        ? Expressions.getParent(currentComponentRender, selectedElement.id)
        : null;

    return {
      nativeId: selectedParentNodeNativeId,
      nativeIdPath: parentPath,
      element: parentElement,
    };
  }
);

/**
 * Returns whether the hovered outline on the canvas should take precedence
 * over the selected parent outline on the canvas.
 *
 * When the hovered node is the selected parent node,
 * or when the hovered node is a child within the selected parent node,
 * we want it take precedence over the parent outline to avoid overlapping outlines
 * We lower the selected parent's z-Index in the following cases:
 * 1. The hovered node is the selected parent node
 * 2. The hovered node is a child of the selected parent node
 */
export const selectShouldHoveredOutlineTakePrecedenceOverSelectedParent: (arg1: {
  UiNodeStore: UiNodeStateType;
  DesignerStore: DesignerStateType;
}) => boolean = createSelector(
  selectSelectedParentNativeIdAndElement,
  (state: {UiNodeStore: UiNodeStateType}) =>
    state.UiNodeStore.hoveredNodeNativeId,
  (selectedParentNativeIdAndElement, hoveredNodeNativeId) => {
    const {element: parentElement} = selectedParentNativeIdAndElement;

    if (parentElement === null || hoveredNodeNativeId === null) return false;

    // Lower the precedence of the parent outline if the hovered node is the selected parent node
    if (hoveredNodeNativeId === parentElement.id) return true;

    const isHoveredNodeChildOfSelectedParent = contains(
      parentElement,
      (el) => el.id === hoveredNodeNativeId
    );

    // Lower the precedence of the parent outline if the hovered node is a child of the selected parent node
    return isHoveredNodeChildOfSelectedParent;
  }
);

/*
  Return body element with all user components instances replaces by their roots without applying overrides
*/
export const getExpandedBody = weakMemo(
  (liveComponentMap: Component.ComponentMap) =>
    weakMemo((components: Component.ComponentMap) => {
      const body = getBody(liveComponentMap);
      if (!body) {
        return null;
      }
      return Expressions.getElement(expandUserComponents(components)(body));
    })
);

export const selectIsSlotDefaultSelection: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => boolean = createSelector(
  selectRootElementWithFocusedInstancesExpanded,
  (state: {UiNodeStore: UiNodeStateType}) =>
    getGuidNullable(state.UiNodeStore.selectedNodeNativeId),
  selectComponents,
  getPluginsFromState,
  selectSelectedNativeIdPath,
  (
    rootElement,
    selectedNodeNativeId,
    componentMap,
    plugins,
    selectedNativeIdPath
  ) => {
    if (!rootElement || !selectedNodeNativeId) {
      return false;
    }
    return isSlotDefaultSelection(
      selectedNativeIdPath,
      componentMap,
      selectedNodeNativeId,
      rootElement,
      plugins
    );
  }
);

/**
 * Returns the element that the user is working with. Differs from `selectSelectedElement`
 * in that it will return the component instance if the selected element is a component instance.
 */
export const selectElementFromSelectedNodeNativeId: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => ExpressionElement | null = createSelector(
  selectRootElementWithFocusedInstancesExpanded,
  (state: {UiNodeStore: UiNodeStateType}) =>
    getGuidNullable(state.UiNodeStore.selectedNodeNativeId),
  selectComponents,
  getPluginsFromState,
  selectSelectedNativeIdPath,
  selectFocusedSymbolNativeIdPath,
  (
    rootElement,
    selectedNodeNativeId,
    componentMap,
    plugins,
    selectedNativeIdPath,
    focusedSymbolNativeIdPath
  ) => {
    if (!rootElement || !selectedNodeNativeId) {
      return null;
    }

    if (
      isSlotDefaultSelection(
        selectedNativeIdPath,
        componentMap,
        selectedNodeNativeId,
        rootElement,
        plugins
      )
    ) {
      const owningInstanceRootPath = resolveSlotDefaultOwningInstanceRootPath(
        selectedNativeIdPath,
        focusedSymbolNativeIdPath,
        componentMap,
        plugins
      );
      const owningInstance = owningInstanceRootPath
        ? getTargetElementOrInstance(
            owningInstanceRootPath,
            componentMap,
            plugins
          )
        : null;
      if (owningInstance) return owningInstance;

      // A default whose component renders nested children is selected at the
      // nested element, so the path runs into that render and
      // `getTargetElementOrInstance` — which only steps back to the instance
      // when the path ends exactly at the component root — would return an
      // element from another component. The derived id already names the
      // default, so look it up there first.
      if (focusedSymbolNativeIdPath.length === 0) {
        const componentBoundary = resolveSlotDefaultComponentBoundary(
          selectedNativeIdPath,
          componentMap,
          plugins
        );
        if (componentBoundary) {
          const selectedComponentInstance = getTargetElementOrInstance(
            componentBoundary.componentRootPath,
            componentMap,
            plugins
          );
          if (selectedComponentInstance) return selectedComponentInstance;
        }
      }

      const context = resolveSlotDefaultPathContext(
        selectedNativeIdPath,
        componentMap,
        plugins
      );
      const hit = context
        ? findSlotDefaultElement(
            plugins,
            componentMap,
            context.ownerComponentType,
            selectedNodeNativeId
          )
        : null;

      return (
        hit?.element ??
        getTargetElementOrInstance(selectedNativeIdPath, componentMap, plugins)
      );
    }

    return Expressions.getElementById(rootElement, selectedNodeNativeId);
  }
);

/**
 * Returns the slot default element the selection names, or null when the
 * selection is not slot default content.
 *
 * Differs from `selectElementFromSelectedNodeNativeId`, which answers what the
 * user is working with and so resolves the instance that OWNS the default when
 * no component is focused into the path. That instance is not what the
 * selection names, so it is rejected here, while an instance living inside the
 * default is kept, since a selection that rolls up to a component root names
 * that instance.
 */
export const selectSlotDefaultElementFromSelection: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => ExpressionElement | null = createSelector(
  selectComponents,
  getPluginsFromState,
  selectSelectedNativeIdPath,
  (state: {UiNodeStore: UiNodeStateType}) =>
    getGuidNullable(state.UiNodeStore.selectedNodeNativeId),
  selectElementFromSelectedNodeNativeId,
  (
    componentMap,
    plugins,
    selectedNativeIdPath,
    selectedNodeNativeId,
    workingElement
  ) => {
    if (!selectedNodeNativeId) return null;

    const context = resolveSlotDefaultPathContext(
      selectedNativeIdPath,
      componentMap,
      plugins
    );
    if (!context) return null;

    const hit = findSlotDefaultElement(
      plugins,
      componentMap,
      context.ownerComponentType,
      selectedNodeNativeId
    )?.element;
    if (hit) return hit;

    // The selection can sit on the root of a component the default holds, or
    // deeper inside that component's render. Neither is in `defaultData`, and
    // slot overrides answer both with the enclosing instance, which is the
    // element the default does hold, one segment before the component root.
    const boundary = resolveSlotDefaultComponentBoundary(
      selectedNativeIdPath,
      componentMap,
      plugins
    );
    const instanceSegment = boundary
      ? boundary.componentRootPath[boundary.componentRootPath.length - 2]
      : undefined;
    const instanceId = instanceSegment
      ? getGuidNullable(instanceSegment)
      : null;
    const instanceHit = instanceId
      ? findSlotDefaultElement(
          plugins,
          componentMap,
          context.ownerComponentType,
          instanceId
        )?.element
      : undefined;
    if (instanceHit) return instanceHit;

    const ownerInstanceId = getGuidNullable(
      context.ownerInstancePath[context.ownerInstancePath.length - 1]
    );

    return workingElement && workingElement.id !== ownerInstanceId
      ? workingElement
      : null;
  }
);

/**
 * If the currently selected element is a component instance, returns
 * the component instance's native id. Otherwise, returns null.
 */
export const selectSelectedComponentInstanceNativeId: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => NativeId | null = createSelector(
  hasSelectedComponentInstance,
  selectSelectedNodeNativeId,
  (isComponentInstance, selectedNodeNativeId) => {
    return isComponentInstance ? selectedNodeNativeId : null;
  }
);

/**
 * If a component instance is selected, returns the instance's root element.
 */
export const selectSelectedComponentInstanceRootElement: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => ExpressionElement | null = createSelector(
  selectComponents,
  selectElementFromSelectedNodeNativeId,
  hasSelectedComponentInstance,
  (liveComponentMap, element, isComponentInstance) => {
    if (!element || !isComponentInstance) {
      return null;
    }
    return getComponentInstRootElement(liveComponentMap)(element) || null;
  }
);

/**
 * Select all user components from the site, that have no origin field
 */
export const selectSiteUserComponents: (arg1: {
  DesignerStore: DesignerStateType;
}) => QualifiedMap<string, Component.ComponentType> = createSelector(
  selectComponents,
  (componentMap) => {
    const componentList = componentMap.filter((component, componentName) => {
      const componentIsUserComponent = isUserComponent(componentName);

      // We only want components with no origin - i.e. local site components
      return componentIsUserComponent && !Component.getOrigin(component);
    });

    return componentList;
  }
);

export const selectComponentInstanceHasCyclicSlotAncestor = createSelector(
  selectSelectedNativeIdPath,
  selectComponents,
  (selectedNativeIdPath, componentMap) => {
    return componentInstanceHasCyclicSlotAncestor(selectedNativeIdPath)(
      componentMap
    );
  }
);

export const selectIsSelectedElementInsideSlot: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => boolean = createSelector(
  selectCurrentComponent,
  selectComponents,
  selectElementFromSelectedNodeNativeId,
  (currentComponent, componentMap, selectedElementOrInstance) => {
    return (
      selectedElementOrInstance !== null &&
      currentComponent &&
      isElementInsideSlot(componentMap)(currentComponent)(
        selectedElementOrInstance
      )
    );
  }
);

/**
 * The slot that resetting acts on for the current selection, or null when there
 * is nothing to reset.
 */
export const selectSlotResetTarget: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => ReturnType<typeof getSlotResetTarget> = createSelector(
  getPluginsFromState,
  selectComponents,
  selectCurrentComponent,
  selectSelectedNativeIdPath,
  selectFocusedSymbolNativeIdPath,
  (
    plugins,
    componentMap,
    currentComponent,
    selectedNativeIdPath,
    focusedSymbolNativeIdPath
  ) =>
    currentComponent && selectedNativeIdPath
      ? getSlotResetTarget(
          plugins,
          componentMap,
          currentComponent,
          focusedSymbolNativeIdPath,
          selectedNativeIdPath
        )
      : null
);

export const selectIsSelectedElementInsideRichText: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => boolean = createSelector(
  selectCurrentComponent,
  selectComponents,
  selectElementFromSelectedNodeNativeId,
  (state: {UiNodeStore: UiNodeStateType}) =>
    state.UiNodeStore.selectedNativeIdPath,
  (state: {UiNodeStore: UiNodeStateType}) =>
    state.UiNodeStore.focusedSymbolNativeIdPath,
  (
    currentComponent,
    componentMap,
    selectedElementOrInstance,
    selectedNativeIdPath,
    focusedSymbolNativeIdPath
  ) => {
    // Slot IDs do not resolve in the page render, so re-anchor their selection at the instance.
    const selectedElement =
      selectedElementOrInstance ??
      getOutermostSymbolInstance(
        selectedNativeIdPath,
        componentMap,
        focusedSymbolNativeIdPath
      );

    return (
      selectedElement !== null &&
      currentComponent &&
      !isRichText(selectedElement) &&
      isRichTextDescendant(
        Component.getRender(currentComponent),
        selectedElement
      )
    );
  }
);

/**
 * Returns true if the selected element or instance is inside a page slot.
 * Returns false if the selected element is itself a page slot.
 */
export const selectIsSelectedElementInsidePageSlot: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => boolean = createSelector(
  selectCurrentComponent,
  selectComponents,
  selectElementFromSelectedNodeNativeId,
  (currentComponent, componentMap, selectedElementOrInstance) => {
    return (
      selectedElementOrInstance !== null &&
      !isComponentOnlyDropTarget(selectedElementOrInstance) &&
      currentComponent &&
      isComponentInsidePageSlot(componentMap, selectedElementOrInstance)
    );
  }
);

/**
 * Returns true if the selection is slot-default content whose owning component
 * instance sits inside a page slot.
 *
 * A slot default lives on the component definition, not in the page render, so
 * `selectIsSelectedElementInsidePageSlot` can never find it. Resolve the instance
 * the builder actually placed on the page and ask about that instead. Nested
 * defaults need the outermost instance rather than the immediate owner, since the
 * immediate owner is another default and is also absent from the page render.
 */
export const selectIsSlotDefaultOwnerInsidePageSlot: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => boolean = createSelector(
  selectIsSlotDefaultSelection,
  selectComponents,
  selectSelectedNativeIdPath,
  selectFocusedSymbolNativeIdPath,
  (
    isSlotDefault,
    componentMap,
    selectedNativeIdPath,
    focusedSymbolNativeIdPath
  ) => {
    if (!isSlotDefault) return false;

    const ownerInstance = getOutermostSymbolInstance(
      selectedNativeIdPath,
      componentMap,
      focusedSymbolNativeIdPath
    );
    return Boolean(
      ownerInstance && isComponentInsidePageSlot(componentMap, ownerInstance)
    );
  }
);

/**
 * Returns true if the page contains at least one Page Slot element,
 * false otherwise.
 */
export const selectPageContainsPageSlot: (arg1: {
  DesignerStore: DesignerStateType;
}) => boolean = createSelector(selectComponents, (componentMap) => {
  const body = getBody(componentMap);

  if (!body) {
    return false;
  }

  return Boolean(findElementDescendant(body, isComponentOnlyDropTarget));
});

/**
 * Returns the name of the currently focused user component.
 *
 * @returns the name of the current focused user component, or `null` if nothing is focused or the current
 * component type is not a user component.
 */
export const selectCurrentFocusedUserComponentName: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => string | null = createSelector(
  selectCurrentComponentType,
  selectComponents,
  (currentComponentType, componentMap) => {
    // Ignore anything that isn't a user component
    if (!isUserComponent(currentComponentType)) return null;

    const currentUserComponent = componentMap.get(currentComponentType);
    return currentUserComponent
      ? Component.getDisplayName(currentUserComponent)
      : null;
  }
);

/**
 * Returns true if we are currently editing a root user component.
 */
export const selectIsUserComponentFocused = createSelector(
  selectCurrentComponentType,
  (currentComponentType) => {
    return isUserComponent(currentComponentType);
  }
);

/**
 * Returns the root user component being edited, or `null` if none is.
 */
export const selectFocusedUserComponent = (
  state: Parameters<typeof selectCurrentComponent>[0]
): Component.ComponentType | null =>
  selectIsUserComponentFocused(state) ? selectCurrentComponent(state) : null;

/**
 * Returns the style variant field ID of a current component, or `null` if none is defined.
 *
 * Note: this currently assumes only a single style variant property is supported and thus looks for
 * the first field id key found on `component.variants`.
 */
export const selectComponentStyleVariantFieldId: (state: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => string | null = createSelector(
  selectCurrentComponent,
  (currentComponent) => {
    const variantFields =
      Component.getVariants(currentComponent) ?? emptyObject;
    return Object.keys(variantFields)[0] ?? null;
  }
);

/**
 * Returns the currently-active variant option ID, if any.
 *
 * A variant option ID corresponds to the ID of the specific variant value.
 *
 * For instance, a variant `mode` might have `light` and `dark` options; in this
 * scenario, the variant option ID would be used to distinguish between `light`
 * and `dark`.
 *
 * Code-wise, the variant option ID corresponds to the "right hand side" of the
 * `<field-id>=<option-id>` encoding, aka the `VariantStateKey`.
 */
export const selectSelectedVariantOptionId = createSelector(
  (state: {ExternalStore: ExternalState}) => state.ExternalStore.variantState,
  (variantState) =>
    variantState != null ? parseVariantStateKey(variantState)[1] : 'base'
);

/**
 * Returns the list of component style variants for the currently selected component,
 * or `null` if none are defined.
 */
export const selectCurrentComponentStyleVariants: (state: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => Component.StyleVariant[] | null = createSelector(
  selectCurrentComponent,
  selectComponentStyleVariantFieldId,
  (currentComponent, variantFieldId) => {
    if (variantFieldId == null) return null;

    const variantFields = Component.getVariants(currentComponent);
    if (variantFields == null) return null;

    const variants = variantFields[variantFieldId];

    return variants ?? null;
  }
);

/**
 * Returns the placeholder base variant name stored in `component.placeholderBaseVariantName`
 * for the current component type.
 */
export const selectCurrentComponentPlaceholderBaseVariantName = createSelector(
  selectCurrentComponent,
  (currentComponent) => {
    return Component.getPlaceholderBaseVariantName(currentComponent);
  }
);

/**
 * Returns the current `VariantDataMap` necessary to properly render HTML/CSS
 * that contains appropriate custom variant attributes/selectors.
 */
export const selectVariantDataMap = createSelector(
  (state: {DesignerStore: DesignerStateType}) =>
    getLiveComponentMap(state.DesignerStore),
  (componentMap) => generateVariantDataMap(componentMap)
);

/**
 * Checks if the currently selected element is a code component instance
 */
export const isSelectedElementCodeComponent: (state: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => boolean = createSelector(
  selectTargetElementOrInstance,
  selectComponents,
  (selectedElement, components) => {
    return selectedElement
      ? isCodeComponentInst(components)(selectedElement)
      : false;
  }
);

/**
 * Checks if the currently selected element is an editable code component
 * instance (has source code that can be modified via the AI Assistant).
 */
const isSelectedElementEditableCodeComponent: (state: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => boolean = createSelector(
  selectTargetElementOrInstance,
  selectComponents,
  (selectedElement, components) => {
    return selectedElement
      ? isEditableCodeComponentInst(components)(selectedElement)
      : false;
  }
);

/**
 * Checks if the currently selected element is a read-only (non-editable)
 * code component instance.
 */
export const isSelectedElementReadOnlyCodeComponent = (state: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) =>
  isSelectedElementCodeComponent(state) &&
  !isSelectedElementEditableCodeComponent(state);

/**
 * Returns true when the user is focused inside a code component.
 */
export const selectIsFocusedInEditableCodeComponent = createSelector(
  selectCurrentComponent,
  selectIsUserComponentFocused,
  (currentComponent, isUserComponentFocused) => {
    if (!isUserComponentFocused) return false;
    return isCodeComponentWithSourceComponent(currentComponent);
  }
);

/**
 * Returns true when the user is editing a code component in context
 * (focused inside a code component on a regular page, NOT on the component canvas).
 */
export const selectIsFocusedInEditableCodeComponentInContext = (
  state: Parameters<typeof selectIsFocusedInEditableCodeComponent>[0] &
    Parameters<typeof selectIsCodeComponentCanvas>[0]
) =>
  selectIsFocusedInEditableCodeComponent(state) &&
  !selectIsCodeComponentCanvas(state);

/**
 * Returns true when the user is editing a code component in any mode
 * (on the component canvas or focused in an editable code component in context).
 */
export const selectIsEditingCodeComponent = (
  state: Parameters<typeof selectIsCodeComponentCanvas>[0] &
    Parameters<typeof selectIsFocusedInEditableCodeComponent>[0]
) =>
  selectIsCodeComponentCanvas(state) ||
  selectIsFocusedInEditableCodeComponent(state);

export const selectIsNotEditingCodeComponent = (
  state: Parameters<typeof selectIsEditingCodeComponent>[0]
) => !selectIsEditingCodeComponent(state);
